import { axiosInstance } from '~/src/config/config_axios'
import {
  IPostRequestOrderMessageRequestBody,
  IPostStatementRequest,
  IRequestOrderDetailResponse,
  IRequestOrderMessageResponse,
  IRequestOrderParams,
  IRequestOrderResponse,
  IRequestOrderCsvParams,
  IStatementParams,
  IStatementResponse,
  IRequestOrderDetailExpandResponse,
  IRequestOrderMakerResponse,
  IRequestOrderCarNameResponse,
  IRequestOrderModelCodeResponse,
  IRequestOrderEngineModelResponse,
  IRequestOrderExportParams,
} from '~/src/repositories/Order/models/Order'
import { convertData } from '~/src/repositories/Order/helper/convertData'
import { convertOrderDetail } from '~/src/repositories/Order/helper/convertOrderDetail'
import { convertStatement } from '~/src/repositories/Order/helper/convertStatement'
import { convertOrderMessage } from './helper/convertOrderMessage'
import { makeQueryUrl } from '~/src/libs'
import { IGetFileUpload } from '~/src/repositories/Invoice/models/Invoice'
import { convertOrderDetailExpand } from './helper/convertOrderDetailExpand'

export class OrderRepository {
  async requestOrder(params?: IRequestOrderParams): Promise<IRequestOrderResponse> {
    const data = await axiosInstance.get(makeQueryUrl('/request-orders', params))
    return convertData(data?.data)
  }
  async requestOrderCsv(params?: IRequestOrderCsvParams): Promise<any> {
    return await axiosInstance.get(makeQueryUrl('/request-orders/csv', params))
  }
  async requestOrderDetail(id: string): Promise<IRequestOrderDetailResponse> {
    const data = await axiosInstance.get(`/request-orders/${id}`)
    return convertOrderDetail(data?.data)
  }
  async requestOrderDetailExpand(id: string): Promise<IRequestOrderDetailExpandResponse> {
    const data = await axiosInstance.get(`/request-orders/${id}/expand`)
    return convertOrderDetailExpand(data?.data)
  }
  async requestOrderMakers(): Promise<IRequestOrderMakerResponse[]> {
    const data = await axiosInstance.get('/request-orders/makers')
    return data.data.map((val) => val.name)
  }
  async requestOrderCarNames(carName: string): Promise<IRequestOrderCarNameResponse[]> {
    const data = await axiosInstance.get(makeQueryUrl('/request-orders/car-names', {carName}))
    return data.data.items.map((val) => val.name)
  }
  async requestOrderModelCodes(modelCode: string): Promise<IRequestOrderModelCodeResponse[]> {
    const data = await axiosInstance.get(makeQueryUrl('/request-orders/model-codes', {modelCode}))
    return data.data.items.map((val) => val.name)
  }
  async requestOrderEngineModels(engineModel: string): Promise<IRequestOrderEngineModelResponse[]> {
    const data = await axiosInstance.get(makeQueryUrl('/request-orders/engine-models', {engineModel}))
    return data.data.items.map((val) => val.name)
  }
  async patchRequestOrderItemRead(id: string): Promise<any> {
    return await axiosInstance.patch(`/request-orders/${id}/read`)
  }
  async patchRequestOrder(
    id: string,
    data: {
      refNo: string
      finalPrice: string
      status: number
      remark?: string
      orderDate: string
      arrivalDate: string
    }
  ): Promise<void> {
    return await axiosInstance.patch(`/request-orders/${id}`, data)
  }

  async postRequestOrder(
    data: {
      refNo: string,
      partsCode: string,
      manufacturer: string,
      carName: string,
      vehicleModel: string,
      engineModel: string,
      drive1: string,
      transmission: string,
      drive2: string,
      year: number,
      orderDetail: string,
      customerId: number,
      supplyType: string,
      orderDate: string,
      arrivalDate: string,
      supplier: string,
      remark: string,
      genuinePartNumber: string,
      engineDetail: string,
      withCatalyst: boolean,
    }
  ): Promise<void> {
    return await axiosInstance.post('/request-orders', data)
  }

  async patchRequestOrderStatus(
    data: {
      ids: number[];
      status: number;
    }
    ): Promise<void> {
      return await axiosInstance.patch(`/request-orders/status`, data)
  }
  async requestOrdersMessage(id: string): Promise<IRequestOrderMessageResponse> {
    const data = await axiosInstance.get(`/request-orders/${id}/messages`)
    return convertOrderMessage(data?.data)
  }
  async postRequestOrderMessage(
    id: number,
    data: IPostRequestOrderMessageRequestBody
  ): Promise<void> {
    return await axiosInstance.post(`/request-orders/${id}/messages`, data)
  }
  async requestDashBoardOrdersMessage(): Promise<IRequestOrderMessageResponse> {
    return await axiosInstance.get(`/request-orders/messages`)
  }
    /**
   * deleteRequestOrderMessages
   * コンタクト注文メッセージ削除
   * @param ids {string[]} 削除対象メッセージidの配列
   */
  async deleteRequestOrderMessages(ids: number[]) {
    const query = ids
      .reduce((queryString: string, id: number) => {
        const str = queryString + `ids[]=${id}&`
        return str
      }, '?')
      .slice(0, -1)
      console.log(`/request-orders/messages${query}`)
    return await axiosInstance.delete(`/request-orders/messages${query}`)
  }
  async statement(params?: IStatementParams): Promise<IStatementResponse> {
    if (params?.keyword === '') delete params.keyword
    const data = await axiosInstance.get('/statements', { params })
    return convertStatement(data?.data)
  }
  async postStatement(data: IPostStatementRequest): Promise<void> {
    return await axiosInstance.post('/statements', data)
  }

  async patchStatement(statementId: number, data: IPostStatementRequest): Promise<void> {
    return await axiosInstance.patch(`/statements/${statementId}`, data)
  }

  async getFile(): Promise<IGetFileUpload['data']> {
    const data = await axiosInstance.get(`/fileupload-url`)
    return data.data
  }

  async putFileUpload(url: string, file: File): Promise<void> {
    if (!file) return
    return axiosInstance.put(url, file, {
      headers: {
        'Content-Type': file.type,
      },
    })
  }

  async importUpdateFile(file: File): Promise<void> {
    const formData = new FormData()
    formData.append('file', file)
    return await axiosInstance.post('/request-orders/upload-update', formData)
  }

  async importEngineFile(file: File): Promise<void> {
    const formData = new FormData()
    formData.append('file', file)
    return await axiosInstance.post('/request-orders/upload-engine', formData)
  }

  async importCkdPartFile(file: File): Promise<void> {
    const formData = new FormData()
    formData.append('file', file)
    return await axiosInstance.post('/request-orders/upload-ckd-part', formData)
  }

  async exportExcel(params?: IRequestOrderExportParams): Promise<any> {
    return await axiosInstance.get(makeQueryUrl('/request-orders/excel', params), {
      responseType: 'arraybuffer',
    })
  }

  async downloadTemplateEngineFile(): Promise<any> {
    return await axiosInstance.get('/request-orders/download-template-engine', { responseType: 'blob'})
  }

  async downloadTemplateCkdPartFile(): Promise<any> {
    return await axiosInstance.get('/request-orders/download-template-ckd-part', { responseType: 'blob'})
  }
}
